/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * HeadersEditor (#851) — the demand-source request-header editor, rebuilt on the
 * shared mapping paradigm (`docs/MAPPING_EDITOR_UX_GUIDANCE.md`). A header is a
 * `key → value-mapping` row whose value resolves through the shared
 * {@link MappingControl} (static / macro / path / expression), committed with
 * {@link RowEditActions}; the standard-header catalog drives Add-recommended; and
 * issues surface through the shared {@link ValidationSummary} (guidance §16).
 *
 * The proxy already forwards the incoming request's headers, so rows here are
 * overrides / additions (see the note below the toolbar).
 */

import { getValueSuggestionsForSurface } from '../inputs/data-model-schema';
import { rowSeverity } from './mapping-issues';
import React from 'react';
import { useExpressionNameLookup } from '../../../hooks/useExpressionLibrary';
import { HEADER_IS_TEXT } from './mapping-refusals';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { FieldHelpTooltip } from '../../ui/FieldHelpTooltip';
import { rowValueResolves } from './mapping-help';
import { MappingControl } from './MappingControl';
import { MappingRow, MappingRowList, RowStateMark, type TemplateRevert } from './MappingRow';
import { ProvenanceLegend } from './ProvenanceLegend';
import { resolutionForHeader } from './resolved-request';
import type { RequestPreview } from '../../../hooks/useRequestPreview';
import { ValidationSummary } from './ValidationSummary';
import {
  type HeaderRow,
  createHeaderRow,
  headerRowToLeaf,
  applyLeafToHeaderRow,
  describeHeaderMapping,
  validateHeaders,
  getMissingStandardHeaders,
  headerRowFromCatalog,
  suggestHeaderNames,
  headerRowForKey,
  suggestHeaderValues,
  describeHeaderRequiredMark,
} from './headers-editor.adapters';
import { leafAddressesChangedExpression, leafDefault } from './mapping-value-round-trip';
import { MappingAutocompleteInput } from '../inputs/MappingAutocompleteInput';
import { rowsAfterApply, rowsAfterCancel, type RowEditSnapshot, type RowOrigin } from './row-edit-snapshot';
import { useRevealOnOpen } from './useRevealOnOpen';
import { rowAnchorId, useFindingReview, useRowOpenRequest, type RowOpenRequest } from './row-open-request';
import type { MappingLeaf } from './ortb-body-editor.adapters';
import type { MacroExpression } from '../../../types/decorator-expression';
import {
  draftRowInheritance,
  rowsWithStoredRow,
  headerRowPaths,
  rowChangedFromStored,
  rowLayerActionKind,
} from '../../../utils/artifact-inheritance';
import { InheritanceMark } from '../InheritanceMark';
import { replaceValueInHeaders, rowOccurrenceMarks, withSurface, type RequestOccurrences } from './request-occurrences';
import { useApplyToAll, type LocalValueChange } from './useApplyToAll';
import { layerActionFor, originMarks, type RowOriginAnswer } from './row-origin-marks';
import { useDraftRevert } from './useDraftRevert';
import type { ArtifactInheritance } from '../../../types';
import { omissionOf, omissionToRow } from '@wire-omission';
import { DRIVER_HEADER_FIELDS, driverHeaderField } from '@destination-field';
import { AutomaticPanel, AutomaticRow } from './AutomaticPanel';
import { ParentRemovalRows, useStopOverriding, type ParentRemovals } from './parent-removals';
import type { SavedLibraryEntry } from './DecoratorExpressionEditor';
import { suggestsSecretValue } from '@sensitive-headers';
import { useSystemDefaults } from '../../../hooks/useConfig';

export interface HeadersEditorProps {
  rows: HeaderRow[];
  onChange: (rows: HeaderRow[]) => void;
  /**
   * **Is this artifact a template** — required and nullable, as on the parameter and body editors. A
   * required header with nothing in it reads *You supply this* on a template, where it is the shape,
   * and *REQUIRED* on an instance (`#1354`). `null` where the surface does not know.
   */
  isTemplate: boolean | null;
  /** When true (default), renders the "+ Add recommended" convenience button. */
  showAddMissingFieldsButton?: boolean;
  /** When embedded in a CollapsibleEditorSection the section owns the title. */
  embedded?: boolean;
  /** Read-only preview: description + rows only, no toolbar/validation/row actions. */
  readOnly?: boolean;
  /**
   * Which of this artifact's rows come from its parent (`P3a`).
   *
   * **Nullable rather than optional, for the reason `preview` is** — an optional prop threaded
   * through a shared component compiles, type-checks and renders nothing, which has already happened
   * four times in this codebase. `null` is a call site saying *this surface has no parent to report*;
   * omitting it entirely would be a call site that forgot.
   */
  inheritance: ArtifactInheritance | null;
  /**
   * **Where else this request sends each row's value** (`#944`) — the index the page builds over
   * parameters, headers and body together. Required and nullable: `null` is a surface with no request
   * to count against, and forgetting it would compile and render nothing.
   */
  requestOccurrences: RequestOccurrences | null;
  /**
   * The rows **as stored**, when this editor is showing a draft over them — see
   * `RequestParameterEditor`. Without it a header the operator has just changed goes on rendering
   * `inherited`, because `inheritance` describes the stored artifact and does not move.
   */
  baselineRows?: HeaderRow[];
  /**
   * **The template this source was copied from, for the way back to it** (Todd, 2026-09-24). A row
   * the operator changed from a template reads `overridden here`, and its revert puts back what the
   * template has for that row **now**. The source stays a copy (`TM9a`); this reads the template at
   * the moment of the revert and tracks nothing afterwards.
   *
   * **Required and nullable** (`#1134`): `null` where the source came from no template, the viewer
   * cannot see it, or the surface is read-only.
   */
  fromTemplate: { name: string; rows: readonly HeaderRow[] } | null;
  /**
   * Give these leaves back to the parent (`P3a`). Omit where reverting is not the gesture — see
   * `RequestParameterEditor`.
   */
  onRevert?: (paths: string[]) => void | Promise<unknown>;
  /**
   * **What this source removes from its parent, by header name** (`#1422`) — the parent's
   * `User-Agent` or `X-Forwarded-For` rows it stopped overriding, so the driver's own value goes out
   * instead. Required and nullable, as on the parameter editor: `null` is a surface with no parent.
   */
  parentRemovals: ParentRemovals<HeaderRow> | null;
  /** The parent's name, when the viewer may know it. */
  parentName?: string;
  /** True while a revert is in flight. */
  isReverting?: boolean;
  /**
   * Where **this subtree** stands relative to the parent, rendered beside the heading.
   *
   * The section header above holds two subtrees — `headers` and `params` — and a chip each put
   * *inherited* twice in one header, which reads as a repeat rather than as two facts (Todd,
   * 2026-08-24). The header now says one thing about the section, and each subtree says its own
   * here, where the operator can see which one it is about.
   */
  sectionMark?: React.ReactNode;
  /**
   * **Save this expression to the shared library** (`#1134`).
   *
   * **Required, and nullable rather than optional** — the same treatment `preview` already has, for
   * the same reason. As `onSaveExpressionToLibrary?:` this compiled, type-checked, passed every spec
   * and **rendered nothing** on the two surfaces that forgot to pass it: `DemandSourceDetailPage`
   * and `SimplifiedDemandSourceWizard`, which is the surface an operator is most likely to build a
   * reusable expression on and the one they reach first.
   *
   * Fifth instance of that failure mode — the provenance work hit it four times in one increment
   * (`#898`) — so the fix is the prop's **type**, not the two call sites: a surface that does not
   * offer the control now has to say so, and one that forgot cannot compile.
   */
  onSaveExpressionToLibrary:
    | ((name: string, description: string, expr: MacroExpression) => Promise<SavedLibraryEntry>)
    | null;
  /**
   * Resolved-request preview (R4a/R4e) — what each header will actually send.
   *
   * **Required, and nullable rather than optional** (2026-08-05). It was `preview?:`,
   * and twice that let a call site simply not pass it: once where *nothing* passed it
   * (`31d3dd63`) and once where edit-mode sites did and read-only sites did not
   * (`e928571f`) — the wrong way round, since R4d says marks matter *more* in
   * read-only. Both compiled and rendered nothing, which is indistinguishable from
   * correctly rendering nothing.
   *
   * `null` is a real answer and means "no source context to resolve against" — the
   * standalone dev harness. It must be written explicitly, so the decision is visible
   * at the call site instead of inferred from an absence.
   */
  preview: RequestPreview | null;
  /**
   * **The row a finding asked this editor to open or reveal** (`#1305`), or `null` where nothing on
   * the surface can point at a row — a read-only preview, or a caller with no findings panel.
   *
   * Required and nullable, the treatment every prop on a shared control carries since `#1134`.
   */
  openRow: RowOpenRequest | null;
}

export const HeadersEditor: React.FC<HeadersEditorProps> = ({
  rows,
  onChange,
  isTemplate,
  showAddMissingFieldsButton = true,
  embedded = false,
  readOnly = false,
  fromTemplate,
  inheritance,
  requestOccurrences,
  baselineRows,
  onRevert,
  parentRemovals,
  parentName,
  isReverting,
  sectionMark,
  onSaveExpressionToLibrary,
  preview,
  openRow,
}) => {
  // **The library is read once, above the rows** (`XS5`, `#1066`): a collapsed row's summary is
  // produced by a pure function, so the name of a shared expression is handed in rather than
  // fetched per row.
  const expressionNameFor = useExpressionNameLookup();
  const occurrences = React.useMemo(
    () => (requestOccurrences ? withSurface(requestOccurrences, 'headers', { headers: rows }) : null),
    [requestOccurrences, rows],
  );
  const applyToAll = useApplyToAll(occurrences);
  // Live-edit model (matches ResponseMappingEditor): Cancel restores the snapshot.
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [snapshot, setSnapshot] = React.useState<RowEditSnapshot<HeaderRow> | null>(null);
  // Whether a value marked secret is stored encrypted on this deployment (`CR7`); `undefined` says nothing.
  const encryptedAtRest = useSystemDefaults().data?.runtimeSecretsEncrypted;

  /** The way back to the template's current row, for a row changed from it — see `fromTemplate`. */
  const templateRevertFor = (row: HeaderRow): TemplateRevert | null => {
    if (readOnly || row.provenance?.kind !== 'overridden') return null;
    const name = row.key.trim().toLowerCase();
    const fromIt = fromTemplate?.rows.find((r) => r.key.trim().toLowerCase() === name);
    if (!fromTemplate || !fromIt) return null;
    return {
      templateName: fromTemplate.name,
      onRevert: () => {
        if (editingId === row.id) {
          setEditingId(null);
          setSnapshot(null);
        }
        onChange(rows.map((r) => (r.id === row.id ? { ...fromIt, id: row.id, provenance: { kind: 'template' } } : r)));
        preview?.commit();
      },
    };
  };

  /** Keyed by header **name** — the same key the inheritance paths use; row ids are re-minted. */
  const storedByKey = React.useMemo(() => new Map((baselineRows ?? []).map((row) => [row.key, row])), [baselineRows]);
  const isRowChanged = React.useCallback(
    (row: HeaderRow) => rowChangedFromStored(storedByKey.get(row.key), row),
    [storedByKey],
  );

  /** Revert in a draft or out of one — see `useDraftRevert` and the parameter editor. */
  const revertFromParent = useDraftRevert({
    onRevert,
    baselineRows,
    rows,
    keyOf: (row: HeaderRow) => row.key,
    // A removal has no row at all, so its name is asked about too — see the parameter editor.
    keysOf: (paths) =>
      [
        ...new Set([
          ...rows.map((r) => r.key),
          ...(baselineRows ?? []).map((r) => r.key),
          ...(parentRemovals?.names ?? []),
        ]),
      ].filter((key) => paths.some((p) => headerRowPaths(key).some((base) => p === base || p.startsWith(`${base}.`)))),
    apply: (nextRows, revertedKeys) => {
      const removals = parentRemovals;
      if (removals?.onChange && removals.names.some((n) => revertedKeys.has(n))) {
        removals.onChange({ names: removals.names.filter((n) => !revertedKeys.has(n)), rows: nextRows });
      } else {
        onChange(nextRows);
      }
    },
  });

  /** The row's inheritance chip and its revert, or its authoring provenance — see the param editor. */
  /**
   * **Revert a draft-only change** — the stored row, which is the parent's, back into the draft
   * (`InheritanceMark.restoreDraft`). `undefined` where nothing is stored under this name.
   */
  const restoreStoredRow = React.useCallback(
    (row: HeaderRow) => {
      const stored = storedByKey.get(row.key);
      if (!stored) return undefined;
      return () => {
        if (editingId === row.id) {
          setEditingId(null);
          setSnapshot(null);
        }
        onChange(rowsWithStoredRow(rows, row, stored));
        preview?.commit();
      };
    },
    [storedByKey, editingId, onChange, rows, preview],
  );

  const rowInheritanceMark = React.useCallback(
    (row: HeaderRow): RowOriginAnswer => {
      const paths = headerRowPaths(row.key);
      const state = draftRowInheritance(inheritance, paths, isRowChanged(row));
      if (!state) return { origin: row.provenance };
      return {
        inheritance: (
          <InheritanceMark
            inheritance={inheritance}
            path={paths[0]}
            state={state}
            parentName={parentName}
            isReverting={isReverting}
            onRevert={revertFromParent ?? undefined}
            revertsNow={baselineRows !== undefined}
            restoreDraft={restoreStoredRow(row)}
          />
        ),
      };
    },
    [inheritance, isRowChanged, revertFromParent, baselineRows, parentName, isReverting, restoreStoredRow],
  );

  const issues = React.useMemo(() => validateHeaders(rows, expressionNameFor), [rows, expressionNameFor]);
  const existingKeys = React.useMemo(
    () => new Set(rows.map((r) => r.key.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const missing = getMissingStandardHeaders(existingKeys);

  // Guidance §22 — see the note in `RequestParameterEditor`.
  const revealRef = useRevealOnOpen<HTMLDivElement>(editingId);

  /**
   * The model has moved, so the held resolution no longer describes it (R4e).
   *
   * This editor refreshed on Apply but never invalidated, which made the mark
   * **assert** rather than admit ignorance: a row being typed is not in the
   * resolved request at all, `resolutionForHeader` finds no key, and "absent" and
   * "resolved to nothing" are the same `undefined` — so a header carrying a
   * perfectly good `{deviceType}` rendered `→ omitted` until Apply. The operator
   * reads that as a broken macro, which is the one thing the mark exists to rule
   * out. `RequestParameterEditor.patchRow` has always done this.
   *
   * Invalidate, never resolve, on a row change: `refresh` reads the source getter
   * through a ref the hook syncs in an **effect**, so resolving in the same tick as
   * `onChange` would resolve the model as it was *before* the change and render the
   * result as current — the staleness bug wearing a different hat. Apply and Cancel
   * keep their `refresh` because by then the change has already been through a
   * render.
   */
  const modelMoved = () => preview?.invalidate();

  /**
   * Naming a header **seeds its value** when nothing has been authored yet (#922 · 2).
   *
   * The name box has autocompleted since #922's first pass, but picking `X-Forwarded-For`
   * from it left the value blank — so the editor demonstrably knew the header (it offered
   * it, with a description) and declined to fill in the one thing it also knew. Guidance
   * §5 is "pre-map added fields, never leave a blank", and this was the clearest place it
   * did not hold.
   *
   * **Only into an untouched row.** A value, a mapping or an expression the operator has
   * already set is theirs, and renaming the header must not silently replace it — the same
   * rule `isUneditedPresetEndpoint` applies to a seeded endpoint. So this seeds on the way
   * from blank to named, and never again.
   */
  const updateKey = (id: string, key: string) => {
    modelMoved();
    onChange(
      rows.map((r) => {
        if (r.id !== id) return r;
        const untouched = r.value.trim() === '' && !r.expression;
        // **Secret is suggested on the same way from blank to named, and only there** (`CR2`, `#1371`): a
        // row nobody has stated an answer on takes the name policy's suggestion, and the operator may untick it.
        const renamed = {
          ...r,
          key,
          ...(untouched && r.secret === undefined && suggestsSecretValue(key) ? { secret: true } : {}),
        };
        if (!untouched) return renamed;
        const seeded = headerRowForKey(key);
        return {
          ...renamed,
          value: seeded.value,
          expression: seeded.expression,
          provenance: seeded.provenance,
        };
      }),
    );
  };
  const setRowLeaf = (id: string, leaf: MappingLeaf) => {
    modelMoved();
    onChange(rows.map((r) => (r.id === id ? applyLeafToHeaderRow(r, leaf) : r)));
  };

  const removeRow = (id: string) => {
    if (editingId === id) {
      setEditingId(null);
      setSnapshot(null);
    }
    onChange(rows.filter((r) => r.id !== id));
    // Discrete change → resolve. `modelMoved` only marks stale, which left every
    // bulk action here honest-but-unresolved forever.
    preview?.commit();
  };

  /** *Stop overriding* a parent's header and its Undo (`#1422`) — shared with the parameter editor. */
  const { stopOverriding, undo: undoStopOverriding } = useStopOverriding<HeaderRow>({
    parentRemovals,
    rows,
    nameOf: (row) => row.key,
    isSameRow: (a, b) => a.id === b.id,
    onRowLeaving: (row) => {
      if (editingId === row.id) {
        setEditingId(null);
        setSnapshot(null);
      }
    },
    commit: () => preview?.commit(),
  });

  /**
   * **What the driver sends with no row authored** (`DRIVER_HEADER_FIELDS`, `4b-ii`) — listed so the
   * header *Stop overriding* hands back is visible where it goes, as the parameter editor lists its
   * automatic fields. A name a row covers, or that is listed as a removal, is not repeated here.
   */
  const driverSent = React.useMemo(() => {
    const covered = new Set(
      [...rows.map((r) => r.key), ...(parentRemovals?.names ?? [])].map((k) => k.trim().toLowerCase()),
    );
    return DRIVER_HEADER_FIELDS.filter((f) => !covered.has(f.name.toLowerCase()));
  }, [rows, parentRemovals]);

  const openEditor = (row: HeaderRow, origin: RowOrigin) => {
    setSnapshot({ row, origin });
    setEditingId(row.id);
  };

  /**
   * **How this editor answers _which row is that_** — a header is named, and `validateHeaders` puts
   * that key in a finding's `ref`. The case-insensitive fallback matters more here than it does for
   * a parameter: HTTP header names are case-insensitive, so `Content-Type` and `content-type` are
   * one header and an operator may have authored either.
   */
  const resolveHeaderRow = (ref: string): HeaderRow | null => {
    const wanted = ref.trim();
    const lower = wanted.toLowerCase();
    return rows.find((r) => r.key.trim() === wanted) ?? rows.find((r) => r.key.trim().toLowerCase() === lower) ?? null;
  };
  // `openEditor`, not `setEditingId` — see the parameter editor: the snapshot is Cancel's subject.
  const openExistingRow = (row: HeaderRow) => openEditor(row, 'existing');
  const findingReview = useFindingReview(
    'headers',
    resolveHeaderRow,
    (ref) => resolveHeaderRow(ref) !== null,
    openExistingRow,
  );
  useRowOpenRequest<HeaderRow>('headers', openRow, resolveHeaderRow, openExistingRow);

  const applyEditor = () => {
    const openId = editingId;
    const held = snapshot;
    const current = rows.find((r) => r.id === openId);
    // See `RequestParameterEditor` — a shared value asks first, and the answer is one commit (`#944`).
    const commit = (change: LocalValueChange | null) => {
      setEditingId(null);
      setSnapshot(null);
      // Open and Apply accepts the row (`#1001`) — see `rowsAfterApply`. No header arrives as a detected
      // guess today; the rule is here so the first one that does is confirmed like every other surface.
      let next = rowsAfterApply(rows, (r) => r.id === openId, held);
      if (change) next = replaceValueInHeaders(next, change);
      if (next !== rows) onChange(next);
      if (change) occurrences?.applyElsewhere?.apply({ ...change, except: 'headers' });
      // Apply is the commit boundary (§13) and therefore the resolve trigger (R4e).
      preview?.refresh();
    };
    if (!current) return commit(null);
    applyToAll.apply({
      before: held && held.origin === 'existing' ? headerRowToLeaf(held.row) : null,
      after: headerRowToLeaf(current),
      self: { surface: 'headers', name: current.key.trim() },
      commit,
    });
  };

  // Cancel restores the row, or removes it where the gesture that opened the editor created it
  // (`#1262`). The rule is `rowsAfterCancel`'s, not this editor's.
  const cancelEditor = () => {
    const held = snapshot;
    setEditingId(null);
    setSnapshot(null);
    if (held) onChange(rowsAfterCancel(rows, held, (r) => r.id === held.row.id));
    preview?.refresh();
  };

  const addBlankRow = () => {
    const next = createHeaderRow();
    onChange([...rows, next]);
    preview?.commit();
    openEditor(next, 'created');
  };

  const addRecommended = () => {
    if (missing.length === 0) return;
    onChange([...rows, ...missing.map((h) => headerRowFromCatalog(h))]);
    preview?.commit();
  };

  return (
    <VStack
      gap={3}
      align="stretch"
      // **Keys belong to the editor, not to the dialog around it** (#1129).
      //
      // The wizard advances a step on Enter and closes on Escape, and its skip list is keyed on
      // *element type* — textarea, button, select, contenteditable — so a plain `<input>` that owns
      // those keys cannot say so. The add-a-parameter box did: Enter added the row **and** advanced
      // the step, which also swallowed the reveal (the row was added; the operator was moved off the
      // phase showing it), and Escape cancelled the add **and** closed the whole wizard.
      //
      // Guarding the container rather than each input is the fix the ORTB body editor already
      // shipped, and it is why the next control added in here needs no ceremony to be correct.
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== 'Escape') return;
        e.stopPropagation();
        // **Escape has to mean something here, or swallowing it is its own surprise** (#1129).
        //
        // Stopping it was correct — it must never reach the wizard and close it — but that left
        // Escape doing *nothing* inside an editor, which Todd reported as surprising: the key had
        // an effect before, and now it does not. So it closes the open row, which is what Cancel
        // does and what Escape means everywhere else. `cancelEditor` restores the pre-edit snapshot,
        // so this reverts rather than discards, and it is a no-op when no row is open.
        if (e.key === 'Escape' && editingId) cancelEditor();
      }}
    >
      <HStack justify={embedded ? 'flex-end' : 'space-between'}>
        {!embedded && (
          <HStack gap={1.5} align="center">
            <Text fontSize="sm" fontWeight="600" color="fg.default">
              HTTP Headers
            </Text>
            {sectionMark}
            {/* The console's one `?` (#1179), replacing a hand-rolled `ⓘ` that a keyboard could not
                reach. It also carries the forwarding paragraph that stood under this title: what the
                surface is, true every visit, and so teaching rather than state. */}
            <FieldHelpTooltip
              ariaLabel="About HTTP Headers"
              content={
                <>
                  The proxy already forwards the incoming request&apos;s headers to this demand source. Add rows here
                  only to override a forwarded header or send a new one — a demand source&apos;s configured headers win
                  over the copied request headers (a request decorator still wins over both).
                  <br />
                  <br />
                  {rowValueResolves('header value')}
                </>
              }
            />
          </HStack>
        )}
        {!readOnly && (
          <HStack gap={1.5}>
            {showAddMissingFieldsButton && missing.length > 0 && (
              <Button size="xs" variant="outline" borderColor="border.default" onClick={addRecommended}>
                + Add recommended
              </Button>
            )}
            <Button size="xs" bg="accent.solid" color="fg.onAccent" _hover={{ bg: 'accent.ink' }} onClick={addBlankRow}>
              + Add header
            </Button>
          </HStack>
        )}
      </HStack>

      {!readOnly && <ValidationSummary issues={issues} onFix={null} review={findingReview} />}

      {rows.length === 0 ? (
        <Box
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="border.default"
          borderRadius="8px"
          p={readOnly ? 3 : 4}
        >
          <Text fontSize="xs" color="fg.muted">
            {readOnly ? 'No custom headers.' : 'No custom headers. Add recommended standards or add one manually.'}
          </Text>
        </Box>
      ) : (
        <MappingRowList>
          <ProvenanceLegend values={rows.map((r) => r.provenance)} />
          {rows.map((row) => {
            const summary = describeHeaderMapping(row, expressionNameFor);
            return (
              <MappingRow
                key={row.id}
                readOnly={readOnly}
                rootRef={editingId === row.id ? revealRef : undefined}
                rootId={rowAnchorId('headers', row.key)}
                editing={editingId === row.id}
                summaryLabel={summary.label}
                summaryKind={summary.kind}
                onEdit={() => openEditor(row, 'existing')}
                onApply={applyEditor}
                onCancel={cancelEditor}
                layerAction={layerActionFor(
                  // **The driver's own headers are declared** (`driverHeaderField`, `4b-ii`), so a
                  // parent's `User-Agent` or `X-Forwarded-For` row displaces an automatic value, and
                  // removing it here gives the driver's back (`#1422`).
                  rowLayerActionKind(inheritance, headerRowPaths(row.key), driverHeaderField(row.key) !== undefined),
                  `header ${row.key || 'row'}`,
                  {
                    remove: () => removeRow(row.id),
                    stopOverriding: stopOverriding ? () => stopOverriding(row) : null,
                  },
                )}
                editLabel={`Edit value for ${row.key || 'header'}`}
                marks={{
                  // See `RequestParameterEditor` — the row's own findings, at the row.
                  severity: rowSeverity(issues, row.key),
                  secret: row.secret === true,
                  // See `RequestParameterEditor` — inheritance wins where it has an opinion, and
                  // carries the way back with it. **No revert**: every header row is typed or comes
                  // from the catalog (`template`), and no intake here detects a mapping (`#1001`).
                  ...originMarks(rowInheritanceMark(row), null, templateRevertFor(row)),
                  // See `RequestParameterEditor` — the #913 mark, supplied for the first time.
                  default: leafDefault(headerRowToLeaf(row), expressionNameFor),
                  sharedChanged: leafAddressesChangedExpression(headerRowToLeaf(row), expressionNameFor),
                  ...rowOccurrenceMarks(occurrences, headerRowToLeaf(row), { surface: 'headers', name: row.key }),
                  // A static header already shows its literal — see `shouldResolve`.
                  ...(preview && (row.expression || /[[{]/.test(row.value ?? ''))
                    ? { resolution: resolutionForHeader(row.key, preview) }
                    : {}),
                  // *Never send it*, or the required claim — one slot, one rendering (`#1421`, `#1354`).
                  state: (
                    <RowStateMark suppressed={!!row.suppress} required={describeHeaderRequiredMark(row, isTemplate)} />
                  ),
                }}
                name={row.key}
                nameInput={
                  /* Type-down on the name (#922): header names are easy to get subtly wrong — casing, hyphenation, `X-Forwarded-For` vs `X-Forwarded-for` — and a wrong one fails silently at the exchange. Filled background for the same reason the other list editors use one: transparent, it read as a label rather than a field. */
                  <MappingAutocompleteInput
                    value={row.key}
                    onChange={(next) => updateKey(row.id, next)}
                    placeholder="Header-Name"
                    size="sm"
                    bg="bg.subtle"
                    minChars={0}
                    getSuggestions={(q) =>
                      suggestHeaderNames(q).map((h) => ({
                        value: h.name,
                        category: 'header',
                        description: h.description,
                      }))
                    }
                  />
                }
              >
                <MappingControl
                  leaf={headerRowToLeaf(row)}
                  onChange={(leaf) => setRowLeaf(row.id, leaf)}
                  onSaveToLibrary={onSaveExpressionToLibrary}
                  /* The outbound context schema is the right source here, and a header value is
                      always text — there is no per-name catalog to declare a type from. Marked for the
                      surface, so a path the driver's session does not carry says so (`#1524`). */
                  pathSuggestions={(q) => getValueSuggestionsForSurface(q, 'macroContext')}
                  pathPlaceholder={null}
                  declaredType={null}
                  // Known values for this header — `X-Forwarded-For` is essentially
                  // always `{ip}`, and typing that from memory is where a subtly wrong
                  // value comes from.
                  staticSuggestions={suggestHeaderValues(row.key)}
                  can={{
                    // `setHeader` skips an empty value, so the driver's own header stands (`empty-value-context.ts`).
                    emptyValue: 'left-off',
                    /* Same session, same vocabulary — a header value resolves from the context the
                       query string does. */
                    acceptsMacro: true,
                    acceptsDerived: true,
                    /* Every value on this surface reads the session, which is what a library expression
                       reads too (`X28a`). */
                    offersLibrary: true,
                    /* A request header is resolved by `BaseDriver.buildRequestHeaders` through
                       `buildMacroContext`, which is the same context the parameter rows use. */
                    resolvesAgainst: 'macroContext',
                    /* An HTTP header is text on the wire, so it is the `scalar` class — the
                       same predicate the parameter rows pass, named here rather than
                       defaulted into (`#1083 · R1`, which landed as a name and not a rule). */
                    surface: 'scalar',
                    /* A header row keeps no scalar type — `applyLeafToHeaderRow` rebuilds a
                       row of `key`/`value`/`macro`/`fallback`/`mappingSource`/`expression`,
                       and an HTTP header is text regardless. The select was rendering and
                       being discarded on Apply, exactly as it was on the parameter rows. */
                    storesValueType: { reason: HEADER_IS_TEXT },
                    /* **Declined, with a reason.** `DemandSourceDto.headers` is a flat
                       name→value map with nowhere to store a rendering. That is R1's design
                       question (a side-car, like `headersProvenance`), not wiring. */
                    sendAs: {
                      reason:
                        'A header is one name and one value — there is nowhere on the row to keep a rendering. Map a structured value through a path or an expression instead.',
                    },
                    /* A header that resolves to nothing is **omitted, not sent empty** (#998,
                       `setHeader`). Not *left literal* — that is what happens to a `{macro}`
                       inside free text, which is the Static tab and has no default to give,
                       and whether it should is `#1178`. */
                    whenUnresolved: 'omit',
                    /* `N11f` gave the structured header value its own `fallback` key, so a
                       header macro can carry a default for the first time. */
                    storesMacroDefault: true,
                    /* **There is something beneath a header row, and since `4b-ii` it can be named**
                     (`X33a`). The merge is *forwarded inbound < driver headers < this row*
                     (`buildRequestHeaders`), and a row that resolves to nothing is omitted rather than
                     sent empty (#998) — so what is beneath goes out. For `User-Agent` and
                     `X-Forwarded-For` that is the driver's own value (`DRIVER_HEADER_FIELDS`); for any
                     other name it is whatever the player sent us under it, if anything.

                     **Still refused**, because declining is not something a header row can store —
                     and it is refused *for a reason on this row*, not as *not yet*: **Never send**,
                     beside it, removes the header from every layer, which is the outcome an operator
                     declining would be after. The sentence names what goes out, not our mechanism. */
                    acceptsSystemDefault: {
                      reason: driverHeaderField(row.key)
                        ? `When this row resolves to nothing, the driver sends its own ${driverHeaderField(row.key)!.name} from the session. To send none at all, choose Never send it.`
                        : 'When this row resolves to nothing, the header the player sent us under this name is forwarded, if there was one. To send none at all, choose Never send it.',
                    },
                    /* **Refused, and for two different reasons that happen to agree** (`P1` slice
                       3). *Optional* cannot mean anything here: a header that resolves to nothing
                       is already omitted unconditionally (#998), so the checkbox would decide
                       nothing. *Never send* is declined deliberately rather than unbuilt — a
                       demand-source header may not suppress an inbound one, which
                       `demand-source-driver.ts` enforces and this surface must not appear to
                       offer a way around. */
                    /* **Never send is offered here; optional is not** (`P1` slice 3, corrected
                       2026-08-27 after Todd's *"I don't want IP passed through — full blind, or
                       privacy blocked"*). Refusing the pair wholesale refused the one that
                       mattered: *never send* is the **only** authoring act that can remove
                       `X-Forwarded-For`, which this driver adds on the operator's behalf and no
                       mapping could reach — mapping a header to nothing omits the row and leaves
                       the protocol default underneath it in place (#998). */
                    /* **Offered since `4b-ii`** (`#1354`). This said *the question does not arise* —
                       *there is no catalogue of headers an exchange demands*. True of a catalogue and
                       not of the question: a template must be able to say *this partner wants an
                       `X-Api-Key` and only you have it* (`TM4`), at every destination, and the header
                       was the one it could not say it at. Required never changes what is sent (`X46`);
                       an instance with nothing here is reported unfilled at Prove. */
                    declaresImportance: {
                      value: row.importance,
                      onChange: (next) => onChange(rows.map((r) => (r.id === row.id ? { ...r, importance: next } : r))),
                    },
                    declaresSecret: {
                      value: row.secret === true,
                      onChange: (next) => onChange(rows.map((r) => (r.id === row.id ? { ...r, secret: next } : r))),
                      encryptedAtRest,
                    },
                    declaresOmission: {
                      value: omissionOf({ optional: true, suppress: row.suppress }),
                      onChange: (next) =>
                        onChange(
                          rows.map((r) => (r.id === row.id ? { ...r, suppress: omissionToRow(next).suppress } : r)),
                        ),
                      refuses: {
                        optional: {
                          reason: 'A header with nothing to send is always left off, so this decides nothing here.',
                        },
                      },
                      /* Nothing layers under a demand source's headers — see `RequestParameterEditor`. */
                      layering: null,
                    },
                  }}
                />
              </MappingRow>
            );
          })}
        </MappingRowList>
      )}
      <AutomaticPanel
        title="Sent automatically"
        count={driverSent.length + (parentRemovals?.names.length ?? 0)}
        action="add a row with this name to override it or never send it"
      >
        <ParentRemovalRows
          removals={parentRemovals}
          parentName={parentName}
          readOnly={readOnly}
          insteadOf={(name) => `the driver's own ${driverHeaderField(name)?.name ?? name}`}
          revert={revertFromParent ?? null}
          revertsNow={baselineRows !== undefined}
          isReverting={isReverting}
          pathsOf={headerRowPaths}
          undo={undoStopOverriding}
        />
        {driverSent.map((field) => (
          <AutomaticRow
            key={field.name}
            name={field.name}
            detail={field.description ?? ''}
            mark={null}
            resolution={null}
          />
        ))}
      </AutomaticPanel>
      {applyToAll.dialog}
    </VStack>
  );
};
